import { useState, useRef, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Send, Plus, Check } from "lucide-react";
import { WidgetBody } from "./ReportWidget";

export default function ReportAiChat({ onClose, hasMgmt }) {
  const qc = useQueryClient();
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const buildMut = useMutation({
    mutationFn: async (prompt) => {
      const history = msgs.slice(-8).map((m) => ({
        role: m.role,
        content: m.role === "user" ? m.content : JSON.stringify({ widget: m.proposal || null, explanation: m.content }),
      }));
      return (await api.post("/reports/ai-build", { prompt, history })).data;
    },
    onSuccess: (d) => {
      if (!d.feasible) return setMsgs((m) => [...m, { role: "assistant", content: d.reason }]);
      setMsgs((m) => [...m, { role: "assistant", content: d.explanation, proposal: { ...d.widget }, preview: d.preview }]);
    },
    onError: (e) => setMsgs((m) => [...m, { role: "assistant", content: e.response?.data?.detail || "AI build failed — try rewording your request." }]),
  });

  const addMut = useMutation({
    mutationFn: async ({ proposal }) => (await api.post("/reports/widgets", proposal)).data,
    onSuccess: (_, { idx }) => {
      qc.invalidateQueries({ queryKey: ["report-widgets"] });
      setMsgs((m) => m.map((x, i) => (i === idx ? { ...x, added: true } : x)));
      toast.success("Report added to the dashboard");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not add the report"),
  });

  const send = () => {
    const prompt = input.trim();
    if (prompt.length < 3 || buildMut.isPending) return;
    setMsgs((m) => [...m, { role: "user", content: prompt }]);
    setInput("");
    buildMut.mutate(prompt);
  };
  const setVis = (idx, v) => setMsgs((m) => m.map((x, i) => (i === idx ? { ...x, proposal: { ...x.proposal, visibility: v } } : x)));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[92vh] flex flex-col" data-testid="ai-chat-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles size={17} className="text-cyan-600" />AI Report Builder</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-3 pr-1 min-h-[200px] max-h-[55vh]" data-testid="ai-chat-thread">
          {msgs.length === 0 && (
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2.5">
              Describe the report you want and the AI builds it live from Compass data — quotes, jobs, deals & stage history, purchase orders,
              expenses, suppliers, clients, bookings, planner tasks and your Annual Goals. e.g. <em>"average days from qualified to quoted"</em>, <em>"dial of won revenue vs our annual revenue goal"</em>,
              <em>"table of PO spend per supplier"</em>. You can refine it in follow-up messages before adding it.
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} data-testid={`ai-chat-msg-${i}`} className={m.role === "user" ? "flex justify-end" : ""}>
              {m.role === "user" ? (
                <div className="bg-slate-900 text-white text-sm rounded-sm px-3 py-2 max-w-[85%]">{m.content}</div>
              ) : (
                <div className="max-w-[95%] space-y-2">
                  {m.content && <div className="bg-slate-50 border border-slate-200 text-sm text-slate-700 rounded-sm px-3 py-2">{m.content}</div>}
                  {m.proposal && (
                    <div className="border border-cyan-200 rounded-sm p-3 bg-white" data-testid={`ai-chat-proposal-${i}`}>
                      <div className="stamp-label text-slate-500">{m.proposal.type} · preview</div>
                      <div className="font-display font-semibold text-sm mt-0.5">{m.proposal.title}</div>
                      {m.proposal.subtitle && <div className="text-[11px] text-slate-400">{m.proposal.subtitle}</div>}
                      <div className="mt-2"><WidgetBody widget={m.proposal} data={m.preview} mini /></div>
                      <div className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-slate-100 flex-wrap">
                        <Select value={m.proposal.visibility} onValueChange={(v) => setVis(i, v)} disabled={m.added}>
                          <SelectTrigger className="rounded-sm h-8 text-xs w-[210px]" data-testid={`ai-visibility-select-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="public">Public — all staff dashboards</SelectItem>
                            <SelectItem value="management" disabled={!hasMgmt}>Management private</SelectItem>
                          </SelectContent>
                        </Select>
                        {m.added ? (
                          <span className="text-emerald-700 text-xs font-semibold flex items-center gap-1" data-testid={`ai-added-${i}`}><Check size={14} />Added</span>
                        ) : (
                          <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 rounded-sm h-8" data-testid={`ai-add-widget-${i}`}
                            disabled={addMut.isPending} onClick={() => addMut.mutate({ proposal: m.proposal, idx: i })}>
                            <Plus size={13} className="mr-1" />Add to dashboard
                          </Button>
                        )}
                        <span className="text-[10.5px] text-slate-400">or type below to refine it</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {buildMut.isPending && (
            <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="ai-chat-building">
              <Sparkles size={13} className="text-cyan-600 animate-pulse" />Building your report from live data…
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className="flex gap-2 pt-2 border-t border-slate-100">
          <Textarea rows={1} value={input} autoFocus
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder='Describe a report… e.g. "jobs completed per month" '
            className="rounded-sm text-sm min-h-[40px] resize-none" data-testid="ai-chat-input" />
          <Button className="bg-cyan-600 hover:bg-cyan-700 rounded-sm self-end" data-testid="ai-chat-send"
            disabled={input.trim().length < 3 || buildMut.isPending} onClick={send}>
            <Send size={15} />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
